import React from 'react';
import { createPortal } from 'react-dom';
import {
    Button, Collapse, FormGroup, UncontrolledTooltip,
} from 'reactstrap';
import { debounce } from 'lodash-es';
import AsyncSelect from 'react-select/async';
import { faAddressCard, faPlus, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { email as emailUtil, patterns } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../../common/ViewModel';
import {
    FlexBetweenRow,
    FormCheckbox, FormLabel, Input, SmallPrimaryButton, toasty,
} from '../../common/forms/FormElements';
import CommonContext, { ApiRoutes, ContactTypes } from '../../Common';
import SlideForm from '../../common/forms/SlideForm';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import EmailRecipients from '../../common/forms/EmailRecipients';
import { TipAlert } from '../../common/TipAlert';
import { Modal } from '../../common/Modal';
import { CustomerPortalAccess } from './CustomerPortalAccess';


const isForeman = (contactTypes) => (contactTypes || []).length ? contactTypes.some((ct) => ct === ContactTypes.Foreman) : false;

export default class OrganizationContactForm extends React.Component {
    static contextType = CommonContext;

    // Works in conjunction with ConstrainContactTypesOnJobEnabled tenant setting.
    // May need to update at some point.
    static filteredContactTypes = ['Foreman', 'Office', 'Supervisor', 'Project Manager', 'Regional Manager', 'Director'];

    constructor(props) {
        super(props);

        this.formRef = React.createRef();
        const stateBase = {
            contactTypes: [],
            currentEmailAddress: null,
            defaultContactTypeId: null,
            ...new BaseFormViewModel(),
        };

        const state = stateBase;
        this.state = state;
        this.onClose = this.onClose.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.onTogglePortalAccess = this.onTogglePortalAccess.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onClose(response) {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onOpen = () => {
        this.context.setFormOpened(true);
        this.resetForm();
    };

    onSubmit = (values) => {
        this.context.setFormOpened(false);
        this.props.onSaveCallback(values);
    };

    onTogglePortalAccess = async (setFieldValue) => {
        const { contact, onChange, onPortalAccessGranted } = this.props;

        // Granting persists the contact, so hold it to the same validation the
        // form's own save applies rather than committing a half-filled record.
        const formEl = document.getElementById(this.props.id || 'contactForm');

        if (formEl && !formEl.checkValidity()) {
            this.setState({ formValidated: true });
            toasty.error('Please correct the highlighted fields before granting access');
            return;
        }

        try {
            const saveResponse = await httpClient.put(
                ApiRoutes.locationContact.update(contact.id),
                contact,
                httpClient.format.none,
            );

            if (!saveResponse.ok) {
                toasty.error('This contact could not be saved, so customer portal access was not granted');
                return;
            }

            const response = await httpClient.put(
                ApiRoutes.locationContact.toggleCustomerPortal(contact.id),
            );

            if (onChange) {
                const event = {
                    target: {
                        name: 'applicationUserId',
                        value: response.id,
                        type: 'string',
                    },
                };

                onChange(event);
            } else if (setFieldValue) {
                setFieldValue('applicationUserId', response.id);
            }

            if (onPortalAccessGranted) onPortalAccessGranted();

            toasty.success('This contact has been granted access to the customer portal');
        } catch (ex) {
            console.log({ ex });
            toasty.error('There was a problem updating this contact');
        }
    };

    onEmailAdded = () => {
        const { currentEmailAddress } = { ...this.state };
        const { contact } = { ...this.props };

        if (currentEmailAddress) {
            const emailAddress = currentEmailAddress.trim();
            const match = emailUtil(emailAddress);
            if (match) {
                if (!(contact.timesheetEmailRecipients ?? []).length) contact.timesheetEmailRecipients = [];
                if (!contact.timesheetEmailRecipients.includes(emailAddress)) contact.timesheetEmailRecipients.push(emailAddress);
                this.setState({
                    currentEmailAddress: null,
                });
            } else {
                toasty.error('Please enter a single valid email address.');
            }
        }
    };

    onEmailChanged = (e) => {
        const val = e.currentTarget.value;
        this.setState({ currentEmailAddress: val });
    };

    onEmailRemoved = (selected) => {
        const { contact } = { ...this.props };
        const inx = contact.timesheetEmailRecipients.findIndex(
            (ea) => ea === selected,
        );

        contact.timesheetEmailRecipients.splice(inx, 1);

        this.setState({ contact });
    };

    async populateState() {
        let [lcts] = await Promise.all([
            this.props.locationContactTypes || httpClient.js(ApiRoutes.locationContactType.all()),
        ]);

        // Filter contact types unless explicitly set to allow all.
        // Slightly wasteful in that we pull all them and then immediately filter.
        // If we wanted to handle this server-side, modify the ApiRoute.

        // https://codesandbox.io/s/wdyiqv?module=/example.tsx&file=/docs/data.ts:176-847
        // isDisabled: true will allow the option to be in the list but not selectable.
        // Useful for when we want to show that an contact has a filtered type but not allow
        // a new contact to use one of the filtered values.

        // Disable a contact type if we aren't allowing all contact types and it's not one of
        // the contact types that "survive" the filtering process.

        const allowAll = !!this.props.allowAllContactTypes;
        lcts.forEach((lct) => lct.isDisabled = !allowAll && !OrganizationContactForm.filteredContactTypes.some((fct) => fct === lct.description));

        // We'll assume sorting is only necessary if we're not filtering contact types.
        // Note: true - false === 1
        if (!allowAll) lcts = lcts.sort((a, b) => a.isDisabled - b.isDisabled);
        this.setState({
            loading: false,
            contactTypes: lcts.map((x) => ({ label: x.description, value: x.id, isDisabled: x.isDisabled })),
            defaultContactTypeId: this.props.defaultContactTypeId,
        });
    }

    loadEmployeeOptions = debounce((inputValue, callback) => {
        if (!inputValue || inputValue.length < 2) {
            callback([]);
            return;
        }

        const { contact } = this.props;
        const existingIds = (contact?.preferredEmployees ?? []).map((pe) => pe.employeeId);

        httpClient.js(ApiRoutes.typeAheads.searchEmployees(inputValue))
            .then((data) => {
                const options = (data || [])
                    .filter((e) => !existingIds.includes(e.value))
                    .map((item) => ({ value: item.value, label: item.label }));
                callback(options);
            })
            .catch(() => callback([]));
    }, 300);

    openPreferredPersonnelModal = () => {
        Modal.show({
            title: 'Add Preferred Employee',
            SaveButtonProps: { label: 'Add', icon: faPlus },
            onSubmit: ({ preferredEmployee }) => {
                this.props.onAddPreferredPersonnel({
                    employeeId: preferredEmployee?.value,
                    employeeName: preferredEmployee?.label,
                });
            },
            body: () => (
                <ValidatedSelect
                    component={AsyncSelect}
                    name="preferredEmployee"
                    label="Employee"
                    placeholder="Search by name..."
                    loadOptions={this.loadEmployeeOptions}
                    isClearable
                    asObject
                    noOptionsMessage={({ inputValue: iv }) => (iv && iv.length >= 2 ? 'No employees found' : 'Type to search...')}
                    required
                    error="An employee selection is required"
                />
            ),
        });
    };

    resetForm() {
        this.setState({ formValidated: false });
    }

    render() {
        const {
            contact: selectedContact, companies, showCompany, readOnly, allowContactTypeEdit, show,
            contactTypeAsPrimitive = false,
        } = this.props;

        const contact = selectedContact || this.props.contact || {};
        const isEdit = Boolean(contact && contact.id);

        const {
            contactTypes,
            formValidated,
            validationMessage,
            errors,
            loading,
            currentEmailAddress,
        } = this.state;

        // Contact Type Edit only allowed if explicitly set and readOnly is false.
        const contactTypeReadOnly = readOnly || !allowContactTypeEdit;
        let contactTypeHelpMessage = null;

        if (contactTypeReadOnly) {
            const hasContactsAccess = this.context.checkUserHasPermission('location_contact.create') || this.context.checkUserHasPermission('location_contact.edit');
            contactTypeHelpMessage = hasContactsAccess ? 'Updates to this field can be done on the Contacts page.' : 'Please contact a system administrator if you need to edit this.';
        } else if (!this.props.allowAllContactTypes) {
            contactTypeHelpMessage = 'Some contact types are unavailable on this page. Please contact a system administrator if you need one that is not available here.';
        }

        const companySelectList = showCompany && companies && companies.length
            ? companies.map((c) => ({
                label: c.companyName,
                value: c.id,
            }))
            : [];

        const formId = this.props.id || 'contactForm';

        return createPortal(
            <SlideForm
                loading={loading}
                show={show}
                id={formId}
                formIcon={faAddressCard}
                formTitle={
                    isEdit
                        ? 'Edit Contact'
                        : 'Add Contact'
                }
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onDelete={this.onDelete}
                errors={errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                readOnly={readOnly}
                initialValues={contact}
                isValid={(valid) => {
                    if (!valid) {
                        toasty.error('An error occurrect while trying to save this form');
                    }
                }}
            >
                {({ setFieldValue }) => (
                    <>
                        <Input
                            label="First Name"
                            id="firstName"
                            name="firstName"
                            required
                            placeholder="Enter First Name (max 100 characters)"
                            value={contact.firstName ?? ''}
                            onChange={this.props.onChange}
                            maxLength="100"
                            error="First Name is required and must be less than 100 characters."
                        />
                        <Input
                            label="Last Name"
                            id="lastName"
                            name="lastName"
                            required
                            placeholder="Enter Last Name (max 100 characters)"
                            value={contact.lastName ?? ''}
                            onChange={this.props.onChange}
                            maxLength="100"
                            error="Last Name is required and must be less than 100 characters."
                        />
                        {showCompany && (
                            <ValidatedSelect
                                id="companyId"
                                name="companyId"
                                label="Company"
                                options={companySelectList}
                                required
                                value={
                                    (companySelectList ?? []).find(
                                        (x) => contact.companyId == x.value,
                                    ) ?? ''
                                }
                                onChange={this.props.onCompanyChanged}
                                error="Company selection is required."
                            />
                        )}

                        <Input
                            label="Title"
                            id="title"
                            name="title"
                            placeholder="Enter Title (max 150 characters)"
                            value={contact.title ?? ''}
                            onChange={this.props.onChange}
                            maxLength="150"
                        />

                        <Input
                            label="Email"
                            id="email"
                            name="email"
                            autoComplete="off"
                            placeholder="ex. name@service.com"
                            type="email"
                            value={contact.email ?? ''}
                            onChange={this.props.onChange}
                            disabled={!!contact.applicationUserId}
                            endAddon={!!contact.applicationUserId && (
                                <>
                                    <FontAwesomeIcon
                                        icon={faTriangleExclamation}
                                        className="text-warning mt-1 mr-2"
                                        id="contact-email-disabled-warning"
                                    />

                                    <UncontrolledTooltip target="contact-email-disabled-warning">
                                        This customer may change their email address from within the customer portal.
                                    </UncontrolledTooltip>
                                </>
                            )}
                        />

                        {!!this.context.tenant.tenantSettings
                            ?.timesheetEmailEnabled && isForeman(contact.contactTypes) && (
                                <>
                                    <FormGroup>
                                        <FormLabel
                                            className="w-100 contract-email-label"
                                            htmlFor="workOrderTypeId"
                                            label="Automated Timesheet Emails"
                                        />

                                        <TipAlert
                                            label="Email addresses entered below will receive an email whenever a timesheet with this foreman is submitted."
                                        />

                                        <EmailRecipients
                                            currentEmailAddress={
                                                currentEmailAddress
                                            }
                                            onEmailAdded={this.onEmailAdded}
                                            onEmailChanged={this.onEmailChanged}
                                            onEmailRemoved={this.onEmailRemoved}
                                            emailAddresses={
                                                contact.timesheetEmailRecipients
                                            }
                                        />
                                    </FormGroup>

                                    <FormGroup className="ml-2 mt-2">
                                        <FormLabel
                                            htmlFor="includeInTimesheetDigests"
                                            label="Nightly Timesheet Digests"
                                        />

                                        <FormCheckbox
                                            id={`${formId}-includeInTimesheetDigests`}
                                            name="includeInTimesheetDigests"
                                            checked={contact.includeInTimesheetDigests || false}
                                            onChange={this.props.onCheckedChanged}
                                            labelText="Include this foreman's timesheets in Timesheet Digests"
                                        />
                                    </FormGroup>
                                </>
                            )}
                        <FormGroup>
                            <Input
                                id="mobilePhone"
                                name="mobilePhone"
                                label="Mobile Phone"
                                placeholder="ex. 555-555-5555"
                                autoComplete="off"
                                pattern={patterns.phone}
                                value={contact.mobilePhone ?? ''}
                                onChange={this.props.onChange}
                                error="Please ensure the phone number is valid (ex. 555-555-5555)."
                            />
                            <FormCheckbox
                                small
                                className="ml-3 mt-2"
                                id={`${formId}-mobilePhoneIsPrimary`}
                                name="mobilePhoneIsPrimary"
                                checked={contact.mobilePhoneIsPrimary || false}
                                onChange={this.props.onCheckedChanged}
                                labelText="Mobile is primary"
                            />

                            <Collapse isOpen={contact.mobilePhone}>
                                <FormCheckbox
                                    small
                                    className="ml-3 mt-2"
                                    id={`${formId}-receiveSmsNotifications`}
                                    name="receiveSmsNotifications"
                                    checked={contact.receiveSmsNotifications || false}
                                    onChange={this.props.onCheckedChanged}
                                    labelText="Receive SMS job notifications"
                                />
                            </Collapse>
                        </FormGroup>

                        <FormGroup>
                            <Input
                                id="officePhone"
                                name="officePhone"
                                label="Office Phone"
                                placeholder="ex. 555-555-5555"
                                autoComplete="off"
                                pattern={patterns.phone}
                                value={contact.officePhone ?? ''}
                                onChange={this.props.onChange}
                                error="Please ensure the phone number is valid (ex. 555-555-5555)."
                            />
                            <FormCheckbox
                                small
                                className="ml-3 mt-2"
                                id={`${formId}-officePhoneIsPrimary`}
                                name="officePhoneIsPrimary"
                                checked={contact.officePhoneIsPrimary || false}
                                onChange={this.props.onCheckedChanged}
                                labelText="Office is primary"
                            />
                        </FormGroup>

                        <ValidatedSelect
                            id="contactTypes"
                            name="contactTypes"
                            label="Contact Type"
                            helpMessage={contactTypeHelpMessage}
                            options={contactTypes}
                            isMulti
                            asPrimitive={contactTypeAsPrimitive}
                            required
                            isDisabled={contactTypeReadOnly}
                            // contactTypes: label == description, value == id
                            value={(contactTypes ?? []).filter((x) =>
                                (contact.contactTypes ?? []).includes(x.value) ?? '')}
                            onChange={this.props.onContactTypeChanged}
                            error="Contact Type selection is required."
                        />

                        <FormGroup>
                            <FormLabel
                                htmlFor="isActive"
                                label="Active?"
                            />
                            <FormCheckbox
                                className="ml-3 mt-2"
                                id={`${formId}-isActive`}
                                name="isActive"
                                checked={contact.isActive || false}
                                onChange={this.props.onCheckedChanged}
                                labelText="Contact is active"
                            />
                        </FormGroup>

                        {Boolean(contact.id) && (
                            <CustomerPortalAccess
                                contactId={contact.id}
                                contactFirstName={contact.firstName}
                                applicationUserId={contact.applicationUserId}
                                hasEmail={Boolean(contact.email)}
                                readOnly={readOnly}
                                onToggle={() => this.onTogglePortalAccess(setFieldValue)}
                            />
                        )}

                        {isForeman(contact.contactTypes) && (
                            <FormGroup className="mt-4">
                                <FlexBetweenRow className="mb-1">
                                    <FormLabel
                                        label="Preferred Employees"
                                        className="mb-0"
                                    />

                                    <div>
                                        <SmallPrimaryButton
                                            onClick={this.openPreferredPersonnelModal}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlus}
                                                className="mr-2"
                                            />
                                            <span>Add</span>
                                        </SmallPrimaryButton>
                                    </div>
                                </FlexBetweenRow>

                                <table className="table table-sm table-bordered">
                                    <thead className="text-muted">
                                        <tr>
                                            <th>Name</th>
                                            <th
                                                className="text-center"
                                                style={{ width: '60px' }}
                                            />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!(contact.preferredEmployees ?? []).length ? (
                                            <tr>
                                                <td
                                                    colSpan="2"
                                                    className="text-center text-muted"
                                                >
                                                    No preferred employees added.
                                                </td>
                                            </tr>
                                        ) : (contact.preferredEmployees ?? []).map((pe) => (
                                            <tr key={pe.employeeId}>
                                                <td>{pe.employeeName}</td>
                                                <td className="text-center">
                                                    <Button
                                                        type="button"
                                                        color="outline-danger"
                                                        size="sm"
                                                        onClick={() => this.props.onRemovePreferredPersonnel(pe.employeeId)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </FormGroup>
                        )}
                    </>
                )}
            </SlideForm>,
            document.body,
        );
    }
}
